import React, { useEffect, useState, useRef } from 'react';
import { BASE_URL } from '../../apis/api';
import speakersBg from '../../images/cover/speakers-bg.jpg';
import { useNavigate } from 'react-router-dom';
import RouteConstants from '../../constant/Routeconstant';
import Loader from './Loader';

const BASE_URL_LINK = BASE_URL || 'http://localhost:5000/api';

function SpeakerSlider() {
    const [speakers, setSpeakers] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const intervalRef = useRef(null);
    const containerRef = useRef(null);
    const [visibleCount, setVisibleCount] = useState(getInitialVisibleCount());
    const navigate = useNavigate();

    // Determine initial visible count based on screen size
    function getInitialVisibleCount() {
        if (typeof window === 'undefined') return 3;
        if (window.innerWidth < 640) return 1;
        if (window.innerWidth < 768) return 2;
        if (window.innerWidth < 1024) return 3;
        if (window.innerWidth < 1280) return 4;
        return 5;
    }

    // Handle window resize
    useEffect(() => {
        const handleResize = () => {
            setVisibleCount(getInitialVisibleCount());
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Fetch speakers from API
    useEffect(() => {
        const fetchSpeakers = async () => {
            try {
                setIsLoading(true);
                const response = await fetch(`${BASE_URL_LINK}/speakers/list-featuredspeaker`);
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const data = await response.json();
                // const speakerData = data.map((item) => item?.speaker) || data?.speakers || data || [];
                setSpeakers(data?.data);
            } catch (error) {
                console.error('Error fetching speakers:', error);
                setError(error.message);
            } finally {
                setIsLoading(false);
            }
        };

        fetchSpeakers();
    }, []);

    // Auto-slide functionality
    useEffect(() => {
        if (speakers.length > visibleCount && !isLoading) {
            startAutoSlide();
        }
        return () => stopAutoSlide();
    }, [speakers.length, visibleCount, isLoading]);

    const startAutoSlide = () => {
        stopAutoSlide();
        if (speakers.length > visibleCount) {
            intervalRef.current = setInterval(() => {
                setCurrentIndex((prev) => (prev + 1) % speakers.length);
            }, 4000);
        }
    };

    const stopAutoSlide = () => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
    };

    const goToNext = () => {
        if (speakers.length > 0) {
            setCurrentIndex((prev) => (prev + 1) % speakers.length);
            startAutoSlide();
        }
    };

    const goToPrev = () => {
        if (speakers.length > 0) {
            setCurrentIndex((prev) => (prev - 1 + speakers.length) % speakers.length);
            startAutoSlide();
        }
    };

    const getVisibleSpeakers = () => {
        if (speakers.length <= visibleCount) return speakers;

        const result = [];
        for (let i = 0; i < visibleCount; i++) {
            const index = (currentIndex + i) % speakers.length;
            result.push(speakers[index]);
        }
        return result;
    };

    const handleMouseEnter = () => stopAutoSlide();
    const handleMouseLeave = () => {
        if (speakers.length > visibleCount && !isLoading) {
            startAutoSlide();
        }
    };

    const handleSpeakerClick = (speaker) => {
        console.log('Navigate to speaker:', speaker.heading);
        // navigate(`/speakerview/${speaker.id}/${speaker.heading}`);
    };

    const handleViewAll = () => {
        navigate(RouteConstants.SPEAKERLISTSITEILFK);
    };

    // Error state
    // if (error) {
    //     return (
    //         <div className="bg-gradient-to-br from-slate-50 via-gray-50 to-blue-50 py-16 px-4">
    //             <div className="max-w-7xl mx-auto text-center">
    //                 <h2 className="text-4xl font-bold text-gray-800 mb-4">Speakers</h2>
    //                 <p className="text-red-600 text-xl">Error loading speakers: {error}</p>
    //                 <button
    //                     onClick={() => window.location.reload()}
    //                     className="mt-4 px-6 py-2 bg-yellow-400 text-gray-800 rounded-lg hover:bg-yellow-500 transition-colors"
    //                 >
    //                     Retry
    //                 </button>
    //             </div>
    //         </div>
    //     );
    // }

    // Loading state
    if (isLoading) {
        return (
            <Loader />
        );
    }

    // Empty state
    if (speakers.length === 0) {
        return (
            <div className="bg-gradient-to-br from-slate-50 via-gray-50 to-blue-50 py-16 px-4">
                <div className="max-w-7xl mx-auto text-center">
                    <h4 className="text-4xl text-black font-bold text-gray-800 mb-4">Speakers</h4>
                    <p className="text-gray-600 text-xl">No speakers available at the moment.</p>
                </div>
            </div>
        );
    }

    const visibleSpeakers = getVisibleSpeakers();
    const centerIndex = Math.floor(visibleCount / 2);

    return (
        // <div className="bg-gradient-to-br from-slate-100 via-gray-50 to-blue-200 py-16 px-4 relative overflow-hidden speakers">
        <div
            className="py-16 px-4 relative overflow-hidden speakers"
            style={{
                backgroundImage: `url(${speakersBg})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat'
            }}
        >

            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-5">
                <div className="absolute top-20 left-10 w-72 h-72 bg-yellow-400 rounded-full mix-blend-multiply filter blur-xl animate-pulse"></div>
                <div className="absolute top-40 right-20 w-72 h-72 bg-blue-400 rounded-full mix-blend-multiply filter blur-xl animate-pulse delay-1000"></div>
                <div className="absolute bottom-20 left-1/2 w-72 h-72 bg-purple-400 rounded-full mix-blend-multiply filter blur-xl animate-pulse delay-2000"></div>
            </div>

            <div className="max-w-7xl mx-auto relative z-10">
                {/* Header */}
                <div className="text-center mb-8 sm:mb-12 md:mb-16">
                    <div className="flex items-center justify-center mb-4 sm:mb-6">
                        <div className="h-px bg-gradient-to-r from-transparent via-yellow-400 to-transparent flex-1 max-w-16 sm:max-w-24 md:max-w-32"></div>
                        <h4 className="text-xl sm:text-2xl md:text-3xl font-semibold text-gray-800 mx-4 sm:mx-6 md:mx-8 relative">
                            <span className="bg-gradient-to-r from-gray-800 via-gray-700 to-gray-600 bg-clip-text text-black">
                                FEATURED SPEAKERS
                            </span>
                            <div className="absolute -bottom-2 sm:-bottom-2 left-1/2 transform -translate-x-1/2 w-10 sm:w-12 md:w-16 h-1 bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-full"></div>
                        </h4>
                        <div className="h-px bg-gradient-to-r from-transparent via-yellow-400 to-transparent flex-1 max-w-16 sm:max-w-24 md:max-w-32"></div>
                    </div>
                    <p className="text-black text-base sm:text-lg md:text-xl max-w-2xl sm:max-w-3xl mx-auto leading-relaxed">
                        Meet our distinguished speakers who will share their expertise and insights
                    </p>
                </div>

                {/* Carousel Container */}
                <div
                    ref={containerRef}
                    className="relative flex justify-center items-center py-4 sm:py-6 md:py-8"
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                >
                    {/* Left Arrow - only show if slidable */}
                    {speakers?.length > visibleCount && (
                        <button
                            onClick={goToPrev}
                            className="absolute left-0 sm:left-2 md:left-4 lg:left-8 top-1/2 transform -translate-y-1/2 z-20 group"
                            aria-label="Previous speakers"
                        >
                            <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 rounded-full bg-white shadow-lg hover:shadow-2xl transition-all duration-300 flex items-center justify-center border-2 border-gray-100 hover:border-yellow-400 group-hover:bg-yellow-50 arrowBtn">
                                <svg
                                    className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-gray-600 group-hover:text-yellow-600 transform group-hover:-translate-x-0.5 transition-all duration-300"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
                                </svg>
                            </div>
                        </button>
                    )}

                    {/* Speakers Container */}
                    <div className={`flex gap-2 sm:gap-3 md:gap-4 lg:gap-6 xl:gap-8 transition-all duration-700 ease-in-out px-8 sm:px-12 md:px-16 lg:px-20 speakerContainer`}>
                        {visibleSpeakers.map((speaker, index) => {
                            const isCenter = index === centerIndex;
                            const isAdjacent = index === centerIndex - 1 || index === centerIndex + 1;

                            // Calculate dynamic sizing based on screen size
                            const cardWidth = isCenter ? 'min-w-[160px] sm:min-w-[180px] md:min-w-[200px]' : 'min-w-[140px] sm:min-w-[160px] md:min-w-[180px]';
                            const cardHeight = isCenter ? 'min-h-[240px] sm:min-h-[260px] md:min-h-[280px]' : 'min-h-[220px] sm:min-h-[240px] md:min-h-[260px]';
                            const imageSize = isCenter ? 'w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32 lg:w-36 lg:h-36' : 'w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 lg:w-32 lg:h-32';

                            return (
                                <div
                                    key={`${speaker.id}-${currentIndex}-${index}`}
                                    className={`transform transition-all duration-700 ease-out group ${isCenter
                                        ? 'scale-110 z-10 -translate-y-2'
                                        : isAdjacent
                                            ? 'scale-100 opacity-90 hover:scale-105'
                                            : 'scale-90 opacity-70 hover:scale-95'
                                        }`}
                                    onClick={() => handleSpeakerClick(speaker.speaker)}
                                >
                                    <div
                                        className={`rounded-2xl sm:rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 p-4 sm:p-5 md:p-6 text-center ${isCenter
                                            ? 'bg-[#efc847] shadow-xl sm:shadow-2xl bg-gradient-to-br'
                                            : 'bg-white border border-transparent'
                                            } ${cardWidth} ${cardHeight}`}
                                    >
                                        {/* Image Container */}
                                        <div className={`relative mb-3 sm:mb-4 mx-auto ${imageSize}`}>
                                            <img
                                                // src={speaker?.photo}
                                                src={`${BASE_URL}/${speaker?.speaker.photo}`}
                                                alt={speaker?.speaker?.heading}
                                                className={`w-full h-full object-cover rounded-full transition-all duration-500 ${isCenter
                                                    ? 'border-yellow-300 shadow-md sm:shadow-lg'
                                                    : 'border-gray-200 group-hover:border-yellow-300'
                                                    }`}
                                                loading="lazy"
                                            />
                                            <div className={`absolute inset-0 rounded-full bg-gradient-to-tr from-yellow-400/30 to-transparent transition-opacity duration-500 ${isCenter ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                                                }`}></div>

                                            {/* Center speaker spotlight effect */}
                                            {isCenter && (
                                                <div className="absolute -inset-2 sm:-inset-3 md:-inset-4 rounded-full bg-gradient-to-r from-yellow-400/20 via-transparent to-yellow-400/20 animate-pulse"></div>
                                            )}
                                        </div>

                                        {/* Speaker Info */}
                                        <div className="space-y-2 sm:space-y-3">
                                            <h3 className={`font-normal text-gray-800 transition-all duration-500 ${isCenter
                                                ? 'text-lg sm:text-xl text-black'
                                                : 'text-base sm:text-lg text-black'
                                                }`}>
                                                {speaker?.speaker?.heading}
                                            </h3>
                                            <div
                                                className={`mx-auto rounded-full transition-all duration-500 ${isCenter
                                                    ? 'bg-white w-8 sm:w-10 md:w-12 h-1'
                                                    : 'bg-yellow-400 w-6 sm:w-8 h-0.5'
                                                    }`}
                                            ></div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Right Arrow - only show if slidable */}
                    {speakers.length > visibleCount && (
                        <button
                            onClick={goToNext}
                            className="absolute right-0 sm:right-2 md:right-4 lg:right-8 top-1/2 transform -translate-y-1/2 z-20 group"
                            aria-label="Next speakers"
                        >
                            <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 rounded-full bg-white shadow-lg hover:shadow-2xl transition-all duration-300 flex items-center justify-center border-2 border-gray-100 hover:border-yellow-400 group-hover:bg-yellow-50 arrowBtn">
                                <svg
                                    className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-gray-600 group-hover:text-yellow-600 transform group-hover:translate-x-0.5 transition-all duration-300"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                                </svg>
                            </div>
                        </button>
                    )}
                </div>

                {/* Progress Indicators */}
                {/* {speakers?.length > visibleCount && (
                    <div className="flex justify-center mt-4 sm:mt-6 md:mt-8 space-x-1 sm:space-x-2">
                        {speakers.map((_, index) => (
                            <button
                                key={index}
                                onClick={() => {
                                    setCurrentIndex(index);
                                    startAutoSlide();
                                }}
                                className={`w-2 h-2 sm:w-3 sm:h-3 rounded-full transition-all duration-300 indicatorBtn ${index === currentIndex
                                    ? 'bg-yellow-400 scale-110 sm:scale-125'
                                    : 'bg-black hover:bg-yellow-500'
                                    }`}
                                aria-label={`Go to speaker ${index + 1}`}
                            />
                        ))}
                    </div>
                )} */}

                {/* View All Button */}
                <div className="text-center mt-8 sm:mt-10 md:mt-12">
                    <button
                        onClick={handleViewAll}
                        className="group relative inline-flex items-center px-6 py-2 sm:px-8 sm:py-3 md:px-10 md:py-4 
        bg-black text-white font-normal rounded-full 
        hover:bg-gradient-to-r hover:from-yellow-500 hover:to-yellow-400 hover:text-gray-900 
        transition-all duration-500 transform hover:scale-105 hover:shadow-xl 
        border-2 border-transparent hover:border-yellow-300 text-sm sm:text-base md:text-lg viewMoreBtn"
                    >
                        <span className="relative z-10 text-sm">More Speakers</span>
                        {/* <svg
                            className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 ml-2 sm:ml-3 transform group-hover:translate-x-1 transition-transform duration-300 relative z-10"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                        </svg> */}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default SpeakerSlider;